import { CheckIcon, XIcon } from '@heroicons/react/solid';
import { Box, Checkbox, Divider, FormControl, FormControlLabel, FormHelperText, Typography } from '@material-ui/core';
import classNames from 'classnames';
import { SGSForm, SGSTooltip } from 'components';
import { useAppContext } from 'core/contexts';
import { Roles } from 'core/models';
import { hasFunctionality } from 'core/utils/roles';
import { useUserDroitsContext } from 'features/utilisateurs/contexts/UtilisateurDroitsProvider';
import { useUtilisateurFromContext } from 'features/utilisateurs/contexts/UtilisateurFormProvider';
import { useUserInfosContext } from 'features/utilisateurs/contexts/UtilisateurInfosProvider';
import { hasRoleRight } from 'features/utilisateurs/utils/utils';
import { PropsWithChildren, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import styles from '../Utilisateurs.module.scss';
import { BtnTool } from '../btnTool';

type Props = {
    errors?: any;
    trigger?: Function;
    setValue?: Function;
    readOnly?: boolean;
    getValues?: Function;
};

export const FormDroits: React.FC<PropsWithChildren<Props>> = ({ errors, trigger, setValue, readOnly, getValues }) => {
    const { t } = useTranslation();
    const { user } = useAppContext();
    const location = useLocation();
    const { control } = useFormContext();
    const { loadingDroits } = useUserDroitsContext();
    const { role } = useUserInfosContext();
    const { selectedUser, userConnected } = useUtilisateurFromContext();
    const [forceUpdate, setForceUpdate] = useState(0);

    const isAdminAndOnPageProfil = () => {
        // user detail
        if (selectedUser.id === user.id && selectedUser.roles.includes('ROLE_ADMIN')) return true;
        // page profil
        if (user.roles.includes('ROLE_ADMIN') && location.pathname === '/utilisateurs/modifier') return true;
        return false;
    };

    const isUserSelectedAdmin = () => {
        return selectedUser.roles.includes('ROLE_ADMIN');
    };

    // Utilisateur can not manage user
    useEffect(() => {
        if (role === 'UTILISATEUR') {
            handleChange(false, 'ROLE_ADD_USER');
            handleChange(false, 'ROLE_EDIT_USER');
        }
    }, []);

    // TODO :: check
    //const diabledOpts = hasRole(user, ['ROLE_ADMIN']) ? rolesCotisation.map((e) => e.value.toLowerCase()) : userConnected.droits.map((e) => e.toLowerCase());

    const allRoles = Object.values(Roles);

    const handleChange = (value, name) => {
        if (isAdminAndOnPageProfil() || readOnly) return;
        let roles = getValues('roles');
        if (value && !roles.includes(name)) {
            roles.push(name);
        } else if (!value && roles.includes(name)) {
            roles = roles.filter((role) => role !== name);
        }
        setValue('roles', roles, { shouldDirty: true });
    };

    const filterRolesByProfil = (roles) => {
        const userRole = getValues('role');
        if (userRole === 'UTILISATEUR') {
            const rolesToRemove = ['ROLE_ADD_USER', 'ROLE_EDIT_USER'];
            roles = roles.filter((role) => !rolesToRemove.includes(role));
        }
        // filter by user connected roles is not admin
        if (!user.roles.includes('ROLE_ADMIN')) {
            roles = roles.filter((r) => user.roles.includes(r));
            const allSeletedRoles = getValues('roles');
            roles = Array.from(roles).concat(allSeletedRoles);
        }
        return roles;
    };

    const selectAllRoles = () => {
        setValue('roles', filterRolesByProfil(allRoles), { shouldDirty: true });
        setForceUpdate((prev) => prev + 1);
    };

    const unselectAllRoles = () => {
        let roles = [];
        if (!user.roles.includes('ROLE_ADMIN')) {
            const selectedRoles = getValues('roles');
            roles = selectedRoles.filter((r) => !user.roles.includes(r));
        }

        setValue('roles', roles, { shouldDirty: true });
        setForceUpdate((prev) => prev + 1);
    };

    function RoleCheckBox({ label, name, disabled, readOnly = false }) {
        return (
            <FormControl>
                <Controller
                    rules={{ required: true }}
                    control={control}
                    name={'roles'}
                    render={() => (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disabled={disabled}
                                        readOnly={readOnly}
                                        checked={getValues('roles').includes(name) || isAdminAndOnPageProfil() || isUserSelectedAdmin()}
                                        onChange={(e) => {
                                            handleChange(e.target.checked, name);
                                            trigger('roles');
                                        }}
                                    />
                                }
                                label={label}
                                className="mb-0"
                            />
                        </>
                    )}
                />
            </FormControl>
        );
    }

    return (
        <>
            {loadingDroits ? (
                <Box>Chargement...</Box>
            ) : (
                <>
                    {!isAdminAndOnPageProfil() && !readOnly && (
                        <Box className={classNames(styles.toolBar)}>
                            <Box flexGrow={1}></Box>
                            <Box flexGrow={1}>
                                <Box display={'flex'}>
                                    <SGSTooltip title="Tout sélectionner">
                                        <BtnTool onClick={selectAllRoles} className={styles.btnTool}>
                                            <CheckIcon className={styles.iconTool} />
                                            <span className={styles.btnText}>Tout sélectionner</span>
                                        </BtnTool>
                                    </SGSTooltip>
                                    <SGSTooltip title="Tout sélectionner">
                                        <BtnTool onClick={unselectAllRoles} className={styles.btnTool}>
                                            <XIcon className={styles.iconTool} />
                                            <span className={styles.btnText}>Tout désélectionner</span>
                                        </BtnTool>
                                    </SGSTooltip>
                                </Box>
                            </Box>
                            <Box flexGrow={1}></Box>
                        </Box>
                    )}
                    <Box className="text-center" style={{ height: '30px' }}>
                        {errors.roles && (
                            <FormHelperText className="text-center m-0 p-0" error>
                                {(errors.roles as any).message}
                            </FormHelperText>
                        )}
                    </Box>
                    <Box key={forceUpdate} className={styles.contrat}>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <Typography component={'h1'}>{t('utilisateur.droits.acces')}</Typography>
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                <Typography component={'h1'}>{t('utilisateur.droits.affiliation')}</Typography>
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <RoleCheckBox
                                    readOnly={readOnly}
                                    label={t('utilisateur.droits.ajouter')}
                                    name={Roles.ROLE_ADD_USER}
                                    disabled={role === 'UTILISATEUR' || !hasRoleRight(userConnected, Roles.ROLE_ADD_USER) ? true : false}
                                />
                                <RoleCheckBox
                                    readOnly={readOnly}
                                    label={t('utilisateur.droits.gerer')}
                                    name={Roles.ROLE_EDIT_USER}
                                    disabled={role === 'UTILISATEUR' || !hasRoleRight(userConnected, Roles.ROLE_EDIT_USER) ? true : false}
                                />
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                {/* <RoleCheckBox
                                    label={t('utilisateur.droits.affilier')}
                                    name={Roles.ROLE_AFFILIATE_NEW_SALARY}
                                    disabled={!hasRoleRight(userConnected, Roles.ROLE_AFFILIATE_NEW_SALARY)}
                                /> */}
                                <RoleCheckBox label={t('utilisateur.droits.bia')} name={Roles.ROLE_SEE_BIA} disabled={!hasRoleRight(userConnected, Roles.ROLE_SEE_BIA)} />
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <Divider className="mb-3"></Divider>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <Typography component={'h1'}>{t('utilisateur.droits.assures')}</Typography>
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                <Typography component={'h1'}>{t('utilisateur.droits.cotisations')}</Typography>
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <RoleCheckBox label={t('utilisateur.droits.voirAssureActif')} name={Roles.ROLE_VALID_ASSURED} disabled={!hasRoleRight(userConnected, Roles.ROLE_VALID_ASSURED)} />
                                <RoleCheckBox label={t('utilisateur.droits.voirAssureInactif')} name={Roles.ROLE_HISTORY_ASSURED} disabled={!hasRoleRight(userConnected, Roles.ROLE_HISTORY_ASSURED)} />
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                <RoleCheckBox label={t('utilisateur.droits.cotisation')} name={Roles.ROLE_CONTRIBUTION_SEE} disabled={!hasRoleRight(userConnected, Roles.ROLE_CONTRIBUTION_SEE)} />
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <Divider className="mb-3"></Divider>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <Typography component={'h1'}>{t('utilisateur.droits.contact')}</Typography>
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                <Typography component={'h1'}>{t('utilisateur.droits.arret')}</Typography>
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <RoleCheckBox label={t('utilisateur.droits.contacter')} name={Roles.ROLE_CONTACT} disabled={!hasRoleRight(userConnected, Roles.ROLE_CONTACT)} />
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                <RoleCheckBox label={t('utilisateur.droits.declarerArret')} name={Roles.ROLE_ADD_WORK_STOPPING} disabled={!hasRoleRight(userConnected, Roles.ROLE_ADD_WORK_STOPPING)} />

                                {hasFunctionality(user, ['DOCUMENTS_LIDL']) && (
                                    <RoleCheckBox
                                        label={t('utilisateur.droits.depotDocumentLidl')}
                                        name={Roles.ROLE_DOCUMENTS_LIDL}
                                        disabled={!hasRoleRight(userConnected, Roles.ROLE_DOCUMENTS_LIDL)}
                                    />
                                )}
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <Divider className="mb-3"></Divider>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <Typography component={'h1'}>{t('utilisateur.droits.documentation')}</Typography>
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                <Typography component={'h1'}>{t('utilisateur.droits.radiations')}</Typography>
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <RoleCheckBox label={t('utilisateur.droits.docs')} name={Roles.ROLE_DOCUMENTATION} disabled={!hasRoleRight(userConnected, Roles.ROLE_DOCUMENTATION)} />
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                <RoleCheckBox label={t('utilisateur.droits.radier')} name={Roles.ROLE_RADIATION} disabled={!hasRoleRight(userConnected, Roles.ROLE_RADIATION)} />
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <Divider className="mb-3"></Divider>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <Typography component={'h1'}>{t('utilisateur.droits.statistiques')}</Typography>
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                <Typography component={'h1'}>{'Prevoyance'}</Typography>
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                {/* <RoleCheckBox label={t('utilisateur.droits.voirEtatsStatistiques')} name={Roles.ROLE_RT_STATISTIC} disabled={!hasRoleRight(userConnected, Roles.ROLE_RT_STATISTIC)} /> */}
                                <RoleCheckBox label={t('utilisateur.droits.voirEtatistiqueEnLigne')} name={Roles.ROLE_STATISTIC} disabled={!hasRoleRight(userConnected, Roles.ROLE_STATISTIC)} />
                            </SGSForm.Col>
                            {/* <SGSForm.Col md={6}>
                                <RoleCheckBox label={t('utilisateur.droits.garanties')} name={Roles.ROLE_SEE_GARANTIE} disabled={!hasRoleRight(userConnected, Roles.ROLE_SEE_GARANTIE)} />
                            </SGSForm.Col> */}
                            <SGSForm.Col md={6}>
                                <RoleCheckBox label={'Dossiers arrêt de travail'} name={Roles.ROLE_DOSSIER_PREV} disabled={!hasRoleRight(userConnected, Roles.ROLE_DOSSIER_PREV)} />
                            </SGSForm.Col>
                        </SGSForm.Row>
                    </Box>
                    <Box>
                        {errors.roles && (
                            <FormHelperText className="text-center" error>
                                {(errors.roles as any).message}
                            </FormHelperText>
                        )}
                    </Box>
                </>
            )}
        </>
    );
};
